<template>
  <v-card outlined class="pa-2 my-4" style="background-color: #fff8e1" v-if="showWarningBox()"> 
    <v-row class="warning-header" align="center" @click="toggleExpand">
      <v-col cols="auto" class="mx-3">
        <h2 style="color:black;">
          {{ $t("deforestation.complianceRiskWarnings") }} ({{ warningCount }})
          <v-tooltip top color="00BD73" max-width="350">
            <template v-slot:activator="{ on, attrs }">
              <v-icon class="icon-with-background primary--text" style="color:#0EBF67" v-bind="attrs" v-on="on">
                mdi-alert-circle
              </v-icon>
            </template>
            <span>{{ $t("deforestation.complianceRiskWarningsInfo") }}</span>
          </v-tooltip>
        </h2>
      </v-col>
      <v-spacer></v-spacer>
      <v-col cols="auto">
        <v-btn icon>
          <v-icon>{{ isExpanded ? "mdi-menu-up" : "mdi-menu-down" }}</v-icon>
        </v-btn>
      </v-col>
    </v-row>
    <v-expand-transition>
      <div v-show="isExpanded">
        <ul class="ma-3">
          <li v-for="(warning, index) in getWarnings" :key="index">
            <p> <span class="warning-title" @click="openIssues(warning)">{{ warningTitle(warning.assessmentName) }}
                :</span> <span>{{ warning.count }}</span> <span>{{ $t("deforestation.issueFound") }}</span></p>
          </li>
        </ul>
      </div>
    </v-expand-transition>

    <v-dialog v-model="issuesDialog" max-width="960" scrollable content-class="issues-dialog">
      <v-card v-if="activeWarning" class="issues-modal">
        <div class="issues-modal__header">
          <div class="d-flex align-start">
            <div>
              <h3 class="issues-modal__title">{{ warningTitle(activeWarning.assessmentName) }}</h3>
              <div class="issues-modal__subtitle">
                {{ $t("deforestation.riskWarningModal.issuesFound", { count: issuesGrandTotal ?? activeWarning.count ?? 0 }) }}
              </div>
            </div>
            <v-spacer></v-spacer>
            <v-btn icon small @click="issuesDialog = false"><v-icon>mdi-close</v-icon></v-btn>
          </div>

          <v-row dense class="mt-3">
            <v-col cols="12" sm="8">
              <v-text-field
                v-model="issuesSearch"
                :placeholder="$t('deforestation.riskWarningModal.searchPlaceholder')"
                prepend-inner-icon="mdi-magnify"
                outlined dense hide-details clearable
                @input="onIssuesSearch"
              />
            </v-col>
            <v-col cols="12" sm="4">
              <v-select
                v-model="issuesIssueCode"
                :items="issueTypeOptions"
                outlined dense hide-details
                @change="reloadIssuesFromFirstPage"
              />
            </v-col>
          </v-row>
        </div>

        <v-progress-linear :active="issuesLoading" indeterminate color="primary" height="2" />

        <v-card-text class="issues-modal__body">
          <div v-if="!issueRows.length" class="issues-empty">
            <v-progress-circular v-if="issuesLoading" indeterminate size="28" width="3" color="primary" />
            <template v-else>
              <v-icon large :color="issuesError ? 'grey' : 'primary'">{{ issuesError ? 'mdi-cloud-alert' : 'mdi-check-circle-outline' }}</v-icon>
              <div class="mt-2">{{ issuesError ? $t("deforestation.riskWarningModal.loadFailed") : $t("deforestation.riskWarningModal.noIssues") }}</div>
            </template>
          </div>

          <div v-else :class="{ 'issues-refreshing': issuesLoading }">
            <div
              v-for="(row, index) in issueRows"
              :key="row.rowKey"
              class="issue-card"
              :class="{ 'issue-card--open': expandedIssue === row.rowKey }"
            >
              <div class="issue-card__head" @click="toggleIssue(row.rowKey)">
                <span class="issue-card__index">{{ String(issuesFrom + index).padStart(2, '0') }}</span>
                <div class="issue-card__farm">
                  <div class="issue-card__farm-name">{{ row.farmName || $t("deforestation.riskWarningModal.unnamedFarm") }}</div>
                  <div class="issue-card__farm-id">{{ $t("deforestation.riskWarningModal.farmId") }} {{ row.farmId || '-' }}</div>
                </div>
                <div class="issue-card__issue">
                  <v-icon small :color="issueMeta(row.issueCode).color" class="mr-2">{{ issueMeta(row.issueCode).icon }}</v-icon>
                  <span>{{ issueText(row, 'title') }}</span>
                </div>
                <span class="status-pill" :class="`status-pill--${issueMeta(row.issueCode).tone}`">
                  {{ $t(`deforestation.riskWarningModal.chips.${issueMeta(row.issueCode).chip}`) }}
                </span>
                <v-icon class="ml-2">{{ expandedIssue === row.rowKey ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
              </div>

              <v-expand-transition>
                <div v-if="expandedIssue === row.rowKey" class="issue-card__body">
                  <div class="issue-summary">
                    <div class="issue-summary__col">
                      <div class="issue-summary__label">{{ $t("deforestation.riskWarningModal.whatHappened") }}</div>
                      <div class="issue-summary__text">{{ issueText(row, 'finding') }}</div>
                    </div>
                    <div class="issue-summary__divider"></div>
                    <div class="issue-summary__col">
                      <div class="issue-summary__label">{{ $t("deforestation.riskWarningModal.whatToDoNext") }}</div>
                      <div class="issue-summary__text">{{ issueText(row, 'action') }}</div>
                    </div>
                    <v-btn outlined small color="primary" class="issue-summary__action" @click.stop="goToWarningStep">
                      {{ $t("deforestation.riskWarningModal.viewStep") }}
                      <v-icon right small>mdi-open-in-new</v-icon>
                    </v-btn>
                  </div>

                  <div class="issue-meta">
                    <span>{{ $t("deforestation.riskWarningModal.issueId") }} <strong>{{ issueId(row) }}</strong></span>
                    <span class="issue-meta__sep"></span>
                    <span>{{ $t("deforestation.riskWarningModal.lastChecked") }} <strong>{{ formatDate(row.checkedAt) }}</strong></span>
                  </div>

                  <div class="history-panel">
                    <div class="d-flex align-center mb-2">
                      <div class="history-panel__title">{{ $t("deforestation.riskWarningModal.checkHistory") }}</div>
                      <v-spacer></v-spacer>
                      <v-btn text small color="primary" @click.stop="copyIssueLog(row)">
                        <v-icon left small>mdi-content-copy</v-icon>{{ $t("deforestation.riskWarningModal.copyLog") }}
                      </v-btn>
                    </div>
                    <div class="history-table">
                      <div class="history-table__row history-table__row--head">
                        <span>{{ $t("deforestation.riskWarningModal.time") }}</span>
                        <span>{{ $t("deforestation.riskWarningModal.event") }}</span>
                        <span>{{ $t("deforestation.riskWarningModal.result") }}</span>
                      </div>
                      <div v-if="!(row.history || []).length" class="history-table__empty">
                        {{ $t("deforestation.riskWarningModal.noHistory") }}
                      </div>
                      <div v-for="(event, eventIndex) in row.history || []" :key="eventIndex" class="history-table__row">
                        <span class="history-table__time">{{ formatDate(event.at) }}</span>
                        <span>{{ eventLabel(event.event) }}</span>
                        <span>
                          <span class="status-pill status-pill--sm" :class="`status-pill--${resultTone(event.result)}`">{{ resultLabel(event.result) }}</span>
                        </span>
                      </div>
                    </div>

                    <div class="tech-toggle" @click.stop="toggleTech(row.rowKey)">
                      <v-icon small class="mr-2">{{ techOpen === row.rowKey ? 'mdi-chevron-down' : 'mdi-chevron-right' }}</v-icon>
                      <div>
                        <div class="tech-toggle__title">{{ $t("deforestation.riskWarningModal.technicalDetails") }}</div>
                        <div class="tech-toggle__hint">{{ $t("deforestation.riskWarningModal.technicalDetailsHint") }}</div>
                      </div>
                    </div>
                    <v-expand-transition>
                      <pre v-if="techOpen === row.rowKey" class="tech-details">{{ technicalDetails(row) }}</pre>
                    </v-expand-transition>
                  </div>
                </div>
              </v-expand-transition>
            </div>
          </div>
        </v-card-text>

        <div class="issues-modal__footer">
          <div class="d-flex align-center flex-wrap">
            <span class="issues-modal__count" v-if="issuesTotal">
              {{ $t("deforestation.riskWarningModal.showing", { from: issuesFrom, to: issuesTo, total: issuesTotal }) }}
            </span>
            <v-spacer></v-spacer>
            <v-pagination
              v-if="issuesPageCount > 1"
              v-model="issuesPage"
              class="issues-pagination"
              :length="issuesPageCount"
              :total-visible="5"
              :disabled="issuesLoading"
              @input="loadIssues"
            ></v-pagination>
          </div>
          <v-divider class="my-3"></v-divider>
          <div class="d-flex align-center">
            <!-- <v-btn text color="primary" :loading="downloadingIssues" :disabled="!issuesTotal" @click="downloadIssueLog">
              <v-icon left>mdi-download</v-icon>{{ $t("deforestation.riskWarningModal.downloadIssueLog") }}
            </v-btn> -->
            <v-spacer></v-spacer>
            <v-btn color="primary" depressed @click="issuesDialog = false">{{ $t("close") }}</v-btn>
          </div>
        </div>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import { debounce } from "lodash";
import DeforestationService from "@/_services/DeforestationService";

const ISSUES_PAGE_SIZE = 5;
// tone drives the status pill colour; chip is the key under riskWarningModal.chips
const ISSUE_META = {
  ocean: { icon: "mdi-waves", color: "red darken-1", prefix: "LOC", short: "OCN", chip: "action_required", tone: "danger" },
  country_mismatch: { icon: "mdi-earth-off", color: "red darken-1", prefix: "LOC", short: "CTY", chip: "action_required", tone: "danger" },
  point_over_4ha: { icon: "mdi-vector-polygon", color: "orange darken-2", prefix: "LOC", short: "AREA", chip: "action_required", tone: "danger" },
  country_not_defined: { icon: "mdi-map-marker-question-outline", color: "red darken-1", prefix: "LOC", short: "NOC", chip: "action_required", tone: "danger" },
  polygon_overlap: { icon: "mdi-vector-intersection", color: "orange darken-2", prefix: "LOC", short: "OVL", chip: "needs_review", tone: "warning" },
  deforestation_risk: { icon: "mdi-alert", color: "orange darken-2", prefix: "DEF", short: "RISK", chip: "needs_review", tone: "warning" },
  assessment_incomplete: { icon: "mdi-file-document-alert-outline", color: "grey darken-1", prefix: "DEF", short: "NA", chip: "check_failed", tone: "neutral" },
  not_assessed: { icon: "mdi-progress-question", color: "grey darken-1", prefix: "DEF", short: "NEW", chip: "not_checked", tone: "neutral" },
  assessment_missing: { icon: "mdi-clipboard-alert-outline", color: "orange darken-2", prefix: "RA", short: "MISS", chip: "not_started", tone: "neutral" },
  assessment_not_approved: { icon: "mdi-clipboard-clock-outline", color: "orange darken-2", prefix: "RA", short: "PEND", chip: "pending_approval", tone: "warning" },
};
const CATEGORY_ISSUE_CODES = {
  location: ["country_not_defined", "ocean", "country_mismatch", "point_over_4ha", "polygon_overlap"],
  eudr: ["deforestation_risk", "assessment_incomplete", "not_assessed"],
  assessment: ["assessment_missing", "assessment_not_approved"],
};
const RESULT_TONES = {
  started: "neutral",
  completed: "success",
  approved: "success",
  needs_review: "warning",
  pending: "warning",
  check_failed: "danger",
  rejected: "danger",
};

export default {
  props: {
    step: {
      type: Number,
      required: true,
    },
    stepOffset:{
      type:Number,
      required:true
    },
    trafficLightComplianceEnabled: {
      type: Boolean,
      default: false,
    },
    highestStepReached:{
      type:Number,
      required:true
    },
    diligenceId: {
      required: false,
      type: Number,
    }
  },
  computed: {
    warningCount() {
      return this.getWarnings?.reduce((acc, warning) => acc + warning.count, 0);
    },
    currentReportDetail(){
      return this.$store.getters["eudrDDS/getDiligenceDetails"]
    },
    warningStepOffset() {
      return this.stepOffset + (this.trafficLightComplianceEnabled ? 1 : 0);
    },
    getWarnings() {
      const warnings = this.currentReportDetail?.riskWarnings;
      if (!warnings?.length) return [];

      let filteredWarnings = [];
      // Bound by the step currently being viewed (not the highest step ever
      // reached) so that navigating back to an earlier step doesn't keep
      // showing warnings that belong to a later step (e.g. EUDR warnings
      // leaking into the Location Verification step after visiting EUDR once).
      const stepToCheck = this.step;
      const toUiStep = (backendStep) => backendStep + this.warningStepOffset;

      for (let uiStep = 2; uiStep <= stepToCheck; uiStep++) {
        const stepWarnings = warnings.filter(
          (warning) => toUiStep(warning.currentStep) === uiStep
        );
        if (stepWarnings.length > 0) {
          filteredWarnings = filteredWarnings.concat(stepWarnings.slice(0, 1));
        }
      }

      const actualStepInWarnings = filteredWarnings.some(
        (warning) => toUiStep(warning.currentStep) === this.step
      );
      if (!actualStepInWarnings) {
        filteredWarnings = filteredWarnings.filter(
          (warning) => toUiStep(warning.currentStep) <= stepToCheck
        );
      }
      return filteredWarnings;
    },
    issuesPageCount() {
      return Math.ceil(this.issuesTotal / ISSUES_PAGE_SIZE);
    },
    issuesFrom() {
      return this.issuesTotal ? (this.issuesPage - 1) * ISSUES_PAGE_SIZE + 1 : 0;
    },
    issuesTo() {
      return Math.min(this.issuesPage * ISSUES_PAGE_SIZE, this.issuesTotal);
    },
    reportId() {
      return Number(this.diligenceId || this.currentReportDetail?.id) || null;
    },
    // Built from the issue types the server actually found (with counts), so any new
    // issue code shows up without a frontend change. Known codes keep a stable order.
    issueTypeOptions() {
      const known = this.activeWarning ? CATEGORY_ISSUE_CODES[this.warningQuery(this.activeWarning).category] || [] : [];
      const counts = new Map((this.issueTypeCounts || []).map(t => [t.issueCode, t.count]));
      if (this.issuesIssueCode && !counts.has(this.issuesIssueCode)) counts.set(this.issuesIssueCode, 0);
      const codes = [...counts.keys()].sort((a, b) => {
        const ia = known.indexOf(a);
        const ib = known.indexOf(b);
        return (ia < 0 ? Infinity : ia) - (ib < 0 ? Infinity : ib) || a.localeCompare(b);
      });
      const total = [...counts.values()].reduce((sum, n) => sum + n, 0);
      return [
        { text: `${this.$t("deforestation.riskWarningModal.allIssueTypes")} (${total})`, value: null },
        ...codes.map(code => ({ text: `${this.issueTitle(code)} (${counts.get(code)})`, value: code })),
      ];
    }
  },
  data() {
    return {
      isExpanded: false,
      issuesDialog: false,
      activeWarning: null,
      issueRows: [],
      issuesTotal: 0,
      issuesPage: 1,
      issuesLoading: false,
      issuesError: false,
      expandedIssue: null,
      techOpen: null,
      issuesSearch: "",
      issuesIssueCode: null,
      issueTypeCounts: [],
      issuesGrandTotal: null,
      downloadingIssues: false,
      issuesRequestSeq: 0,
    };
  },
  created() {
    this.onIssuesSearch = debounce(this.reloadIssuesFromFirstPage, 400);
  },
  beforeDestroy() {
    this.onIssuesSearch?.cancel?.();
  },

  methods: {
     showWarningBox() {
      const skipTrafficLightStep = this.trafficLightComplianceEnabled && this.step === 3;
      return this.getWarnings && this.getWarnings?.length > 0 && this.step !== 1 && this.step !== 2 && !skipTrafficLightStep && this.currentReportDetail?.enableOnScreenRiskWarnings;
    },
    warningTitle(assessmentName) {
      if(assessmentName === 'Location Verification'){
        return this.$t(`deforestation.locationVerification`);
      } else if(assessmentName === 'EUDR Deforestation Assessment'){
        return this.$t(`deforestation.EUDRDeforestationAssessment`);
      }
      return assessmentName;
    },
    toggleExpand() {
      this.isExpanded = !this.isExpanded;
    },

    getWarningMessage(warning) {
      if(typeof warning === 'object') return warning.message;
      return warning;
    },
    gotoTab(tabName) {
      this.$emit('gotoTab', tabName);
    },
    warningQuery(warning) {
      if (warning.assessmentName === 'Location Verification') return { category: 'location' };
      if (warning.assessmentName === 'EUDR Deforestation Assessment') return { category: 'eudr' };
      return { category: 'assessment', assessmentId: Number(warning.id) || null };
    },
    openIssues(warning) {
      if (!this.reportId) {
        this.gotoTab(warning.assessmentName);
        return;
      }
      this.activeWarning = warning;
      this.issueRows = [];
      this.issuesTotal = warning.count || 0;
      this.issuesPage = 1;
      this.issuesSearch = "";
      this.issuesIssueCode = null;
      this.issueTypeCounts = [];
      this.issuesGrandTotal = null;
      this.expandedIssue = null;
      this.techOpen = null;
      this.issuesDialog = true;
      this.loadIssues();
    },
    reloadIssuesFromFirstPage() {
      this.issuesPage = 1;
      this.loadIssues();
    },
    async fetchIssuesPage(page, limit) {
      const res = await DeforestationService.getRiskWarningDetails({
        reportId: this.reportId,
        ...this.warningQuery(this.activeWarning),
        page,
        limit,
        search: this.issuesSearch,
        issueCode: this.issuesIssueCode,
      });
      if (res?.errors?.length) throw new Error(res.errors[0].message);
      return res?.data?.diligenceReportRiskWarningDetails || { totalCount: 0, rows: [] };
    },
    async loadIssues() {
      const seq = ++this.issuesRequestSeq;
      this.issuesLoading = true;
      this.issuesError = false;
      try {
        const result = await this.fetchIssuesPage(this.issuesPage, ISSUES_PAGE_SIZE);
        if (seq !== this.issuesRequestSeq) return;
        this.issueRows = result.rows || [];
        this.issuesTotal = result.totalCount || 0;
        this.issueTypeCounts = result.issueTypes || [];
        // Location details also list polygon overlaps, which the badge count leaves out, so the
        // header shows what the list really contains (captured while no search is applied).
        if (!(this.issuesSearch || '').trim()) {
          this.issuesGrandTotal = this.issueTypeCounts.reduce((sum, t) => sum + (t.count || 0), 0);
        }
        this.expandedIssue = this.issueRows[0]?.rowKey || null;
        this.techOpen = null;
      } catch (e) {
        if (seq !== this.issuesRequestSeq) return;
        this.issueRows = [];
        this.issuesTotal = 0;
        this.issuesError = true;
      } finally {
        if (seq === this.issuesRequestSeq) this.issuesLoading = false;
      }
    },
    toggleIssue(rowKey) {
      this.expandedIssue = this.expandedIssue === rowKey ? null : rowKey;
      this.techOpen = null;
    },
    toggleTech(rowKey) {
      this.techOpen = this.techOpen === rowKey ? null : rowKey;
    },
    issueMeta(code) {
      return ISSUE_META[code] || { icon: "mdi-alert-circle-outline", color: "orange darken-2", prefix: "ISS", chip: "needs_review", tone: "warning" };
    },
    issueTitle(code) {
      const key = `deforestation.riskWarningModal.issues.${code}.title`;
      if (this.$te(key) || this.$te(key, 'en')) return this.$t(key);
      const words = String(code || '').replace(/[_-]+/g, ' ').trim();
      return words.charAt(0).toUpperCase() + words.slice(1);
    },
    issueText(row, part) {
      const key = `deforestation.riskWarningModal.issues.${row.issueCode}.${part}`;
      if (!this.$te(key) && !this.$te(key, 'en')) return part === 'title' ? this.issueTitle(row.issueCode) : '-';
      return this.$t(key, {
        detected: row.detectedCountry || '-',
        expected: row.expectedCountry || '-',
        area: row.areaHa ?? '-',
        status: row.status || '-',
        overlaps: row.overlapsWith || '-',
      });
    },
    issueId(row) {
      const id = row.diligenceReportProductionPlaceId || row.productionPlaceId || row.farmId || '';
      const meta = this.issueMeta(row.issueCode);
      return [meta.prefix, meta.short, id].filter(Boolean).join('-');
    },
    eventLabel(event) {
      const key = `deforestation.riskWarningModal.events.${event}`;
      return this.$te(key) || this.$te(key, 'en') ? this.$t(key) : event;
    },
    resultLabel(result) {
      if (!result) return '-';
      const key = `deforestation.riskWarningModal.results.${String(result).toLowerCase()}`;
      return this.$te(key) || this.$te(key, 'en') ? this.$t(key) : result;
    },
    resultTone(result) {
      return RESULT_TONES[String(result || '').toLowerCase()] || "neutral";
    },
    formatDate(value) {
      if (!value) return '-';
      const date = new Date(isNaN(value) ? value : Number(value));
      if (isNaN(date.getTime())) return '-';
      const locale = this.$i18n?.locale === 'id' ? 'id-ID' : 'en-GB';
      return date.toLocaleString(locale, {
        day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false,
      });
    },
    technicalDetails(row) {
      return [
        ['issueCode', row.issueCode],
        ['farmId', row.farmId],
        ['productionPlaceId', row.productionPlaceId],
        ['reportProductionPlaceId', row.diligenceReportProductionPlaceId],
        ['status', row.status],
        ['detectedCountry', row.detectedCountry],
        ['expectedCountry', row.expectedCountry],
        ['areaHa', row.areaHa],
        ['overlapsWith', row.overlapsWith],
      ].filter(([, v]) => v !== null && v !== undefined && v !== '')
        .map(([k, v]) => `${k}: ${v}`)
        .join('\n');
    },
    issueLogText(row) {
      const t = (k) => this.$t(`deforestation.riskWarningModal.${k}`);
      const lines = [
        `${this.issueText(row, 'title')} — ${row.farmName || t('unnamedFarm')} (${t('farmId')} ${row.farmId || '-'})`,
        `${t('issueId')}: ${this.issueId(row)}`,
        `${t('lastChecked')}: ${this.formatDate(row.checkedAt)}`,
        `${t('whatHappened')}: ${this.issueText(row, 'finding')}`,
        `${t('whatToDoNext')}: ${this.issueText(row, 'action')}`,
      ];
      if ((row.history || []).length) {
        lines.push('', `${t('checkHistory')}:`);
        row.history.forEach(e => lines.push(`${this.formatDate(e.at)}  ${this.eventLabel(e.event)}  ${this.resultLabel(e.result)}`));
      }
      lines.push('', `${t('technicalDetails')}:`, this.technicalDetails(row));
      return lines.join('\n');
    },
    async copyIssueLog(row) {
      const text = this.issueLogText(row);
      try {
        await navigator.clipboard.writeText(text);
      } catch (e) {
        const area = document.createElement('textarea');
        area.value = text;
        document.body.appendChild(area);
        area.select();
        document.execCommand('copy');
        document.body.removeChild(area);
      }
      this.$notify({ text: this.$t("deforestation.riskWarningModal.logCopied"), type: 'success' });
    },
    goToWarningStep() {
      const name = this.activeWarning?.assessmentName;
      this.issuesDialog = false;
      if (name) this.gotoTab(name);
    },
    async downloadIssueLog() {
      if (!this.activeWarning) return;
      this.downloadingIssues = true;
      try {
        const pageSize = 100;
        const rows = [];
        let page = 1;
        let total = Infinity;
        while (rows.length < total) {
          const result = await this.fetchIssuesPage(page, pageSize);
          total = result.totalCount || 0;
          if (!result.rows?.length) break;
          rows.push(...result.rows);
          page++;
        }
        const escape = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
        const header = ['#', 'Farm name', 'Farm ID', 'Issue', 'Status', 'What happened', 'What to do next', 'Issue ID', 'Last checked', 'Check history', 'Technical details'];
        const lines = rows.map((row, i) => [
          i + 1,
          row.farmName || '',
          row.farmId || '',
          this.issueText(row, 'title'),
          this.$t(`deforestation.riskWarningModal.chips.${this.issueMeta(row.issueCode).chip}`),
          this.issueText(row, 'finding'),
          this.issueText(row, 'action'),
          this.issueId(row),
          this.formatDate(row.checkedAt),
          (row.history || []).map(e => `${this.formatDate(e.at)} ${this.eventLabel(e.event)} (${this.resultLabel(e.result)})`).join('; '),
          this.technicalDetails(row).replace(/\n/g, '; '),
        ].map(escape).join(','));
        const blob = new Blob(['\ufeff' + [header.map(escape).join(','), ...lines].join('\n')], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        const slug = this.warningTitle(this.activeWarning.assessmentName).replace(/[^a-z0-9]+/gi, '-').toLowerCase();
        link.href = url;
        link.download = `dds-${this.reportId}-${slug}-issues.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } catch (e) {
        this.$notify({ text: this.$t("deforestation.riskWarningModal.loadFailed"), type: 'error' });
      } finally {
        this.downloadingIssues = false;
      }
    }
  },
};
</script>
<style scoped lang="scss">
$border: #e3e7ea;
$muted: #6b7280;
$ink: #1f2937;
$green: #0ebf67;

.warning-title {
  font-size: 16px;
  font-weight: 700;
  text-decoration: underline ;
  color: var(--primary-color);
  text-transform: capitalize;
  cursor: pointer;
}
.warning-header{
  cursor: pointer;
}
.info-icon{
  margin-left: 10px;
  margin-bottom: 10px;
}

.issues-modal {
  border-radius: 12px !important;
  overflow: hidden;
}
.issues-modal__header {
  padding: 20px 24px 16px;
}
.issues-modal__title {
  font-size: 20px;
  font-weight: 700;
  color: $ink;
  line-height: 1.3;
}
.issues-modal__subtitle {
  font-size: 14px;
  color: $muted;
  margin-top: 2px;
}
.issues-modal__body {
  padding: 4px 24px 8px !important;
  background: #fff;
}
.issues-modal__footer {
  padding: 12px 24px 16px;
  border-top: 1px solid $border;
}
.issues-modal__count {
  font-size: 13px;
  color: $muted;
}
.issues-empty {
  text-align: center;
  padding: 48px 0;
  color: $muted;
}
.issues-refreshing {
  opacity: 0.55;
  pointer-events: none;
  transition: opacity 0.15s;
}

.issue-card {
  border: 1px solid $border;
  border-radius: 10px;
  margin-top: 10px;
  background: #fff;
  transition: border-color 0.15s, box-shadow 0.15s;
  &:hover {
    border-color: #cfd6db;
  }
}
.issue-card--open {
  border-color: $green;
  box-shadow: 0 2px 10px rgba(14, 191, 103, 0.12);
  &:hover {
    border-color: $green;
  }
}
.issue-card__head {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 12px 16px;
  cursor: pointer;
}
.issue-card__index {
  width: 24px;
  font-weight: 600;
  color: $muted;
  font-variant-numeric: tabular-nums;
}
.issue-card__farm {
  flex: 0 1 34%;
  min-width: 0;
}
.issue-card__farm-name {
  font-weight: 600;
  color: $ink;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.issue-card__farm-id {
  font-size: 12px;
  color: $muted;
}
.issue-card__issue {
  flex: 1 1 auto;
  display: flex;
  align-items: center;
  color: $ink;
  min-width: 0;
}
.issue-card__body {
  padding: 0 16px 16px;
}

.status-pill {
  display: inline-flex;
  align-items: center;
  white-space: nowrap;
  padding: 3px 12px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.5;
}
.status-pill--sm {
  padding: 1px 10px;
  font-weight: 500;
}
.status-pill--warning { background: #fdf0c4; color: #8a5a00; }
.status-pill--danger { background: #fde2e1; color: #b42318; }
.status-pill--neutral { background: #e9ecef; color: #4b5563; }
.status-pill--success { background: #d9f5e6; color: #0f7a45; }

.issue-summary {
  display: flex;
  align-items: flex-start;
  gap: 20px;
  padding: 14px 0 16px;
  border-top: 1px solid $border;
}
.issue-summary__col {
  flex: 1 1 0;
  min-width: 0;
}
.issue-summary__divider {
  width: 1px;
  align-self: stretch;
  background: $border;
}
.issue-summary__label {
  font-weight: 700;
  color: $ink;
  margin-bottom: 4px;
}
.issue-summary__text {
  color: #4b5563;
  font-size: 14px;
  line-height: 1.5;
}
.issue-summary__action {
  align-self: center;
  text-transform: none;
  letter-spacing: 0;
}

.issue-meta {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  font-size: 13px;
  color: $muted;
  padding: 10px 0 14px;
  border-top: 1px solid $border;
  strong {
    color: $ink;
    font-weight: 600;
    margin-left: 6px;
  }
}
.issue-meta__sep {
  width: 1px;
  height: 14px;
  background: #cfd6db;
}

.history-panel {
  background: #f5f7f8;
  border-radius: 10px;
  padding: 12px 14px 14px;
}
.history-panel__title {
  font-weight: 700;
  color: $ink;
}
.history-table {
  background: #fff;
  border: 1px solid $border;
  border-radius: 8px;
  overflow: hidden;
}
.history-table__row {
  display: grid;
  grid-template-columns: 30% 1fr 30%;
  align-items: center;
  padding: 8px 14px;
  font-size: 13px;
  color: $ink;
  & + & {
    border-top: 1px solid #eef1f3;
  }
}
.history-table__row--head {
  background: #f0f3f5;
  font-weight: 600;
  color: $muted;
}
.history-table__time {
  font-variant-numeric: tabular-nums;
}
.history-table__empty {
  padding: 12px 14px;
  font-size: 13px;
  color: $muted;
  border-top: 1px solid #eef1f3;
}

.tech-toggle {
  display: flex;
  align-items: center;
  margin-top: 10px;
  padding: 10px 12px;
  background: #fff;
  border: 1px solid $border;
  border-radius: 8px;
  cursor: pointer;
}
.tech-toggle__title {
  font-weight: 600;
  color: $ink;
  font-size: 14px;
}
.tech-toggle__hint {
  font-size: 12px;
  color: $muted;
}
.tech-details {
  margin: 8px 0 0;
  padding: 10px 12px;
  background: #1f2937;
  color: #e5e7eb;
  border-radius: 8px;
  font-size: 12px;
  line-height: 1.6;
  white-space: pre-wrap;
  word-break: break-word;
}

.issues-pagination ::v-deep .v-pagination__item,
.issues-pagination ::v-deep .v-pagination__navigation {
  box-shadow: none;
  border: 1px solid $border;
  border-radius: 8px;
  min-width: 34px;
  height: 34px;
}
.issues-pagination ::v-deep .v-pagination__item--active {
  background: #fff !important;
  color: $green !important;
  border-color: $green;
  font-weight: 700;
}

@media (max-width: 700px) {
  .issue-card__head {
    flex-wrap: wrap;
    gap: 8px 12px;
  }
  .issue-card__farm {
    flex-basis: calc(100% - 60px);
  }
  .issue-summary {
    flex-direction: column;
  }
  .issue-summary__divider {
    display: none;
  }
}
</style>
